import React, { useEffect, useState } from 'react';
import { Field, Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import styles from 'src/app/screens/Login/Login.module.scss';
import Button, { ButtonWrapper } from 'src/app/components/Button/Button';
import { FormControl, FormField, FormLabel, FormRow, FormStep, FormFeedback, FormCustomSelect, FormTerms } from 'src/app/components/Form/Form';
import PasswordStrength from 'src/app/components/PasswordStrength';
import { useParams, useNavigate } from 'react-router-dom';
import { useMask } from '@react-input/mask';
import { LoginLayout } from 'src/app/screens/Login/Login';
import { validateCPF } from 'src/app/utils/validateCPF';
import toast from 'react-hot-toast';
import api from 'src/app/services/api';
import { createStudent, formatStudentData } from 'src/app/services/auth';
import { HiOutlineLightBulb } from 'react-icons/hi';
import { t } from 'i18next';

export const checkEmail = async email => {
  try {
    return await api.post(`/users/check_email?email=${email}`);
  } catch (error) {
    return error.response;
  }
};

export const checkUserCpf = async cpf => {
  try {
    return await api.get(`/canaleducacao/users/${cpf}`);
  } catch (error) {
    return error.response;
  }
};

const cpfRequired = process.env.REACT_APP_REQUIRED_CPF;

const nameSchema = Yup.object().shape({
  name: Yup.string().required(t('warning.requiredField')),
  cellPhone: Yup.string()
    .required(t('warning.requiredField'))
    .matches(/^\([1-9]{2}\) [9]{1}[0-9]{4}-[0-9]{4}$/, 'Telefone inválido'),
  cpf:
    cpfRequired === 'true'
      ? Yup.string()
          .required(t('warning.requiredField'))
          .test('cpf', 'CPF Inválido', value => validateCPF(value))
      : Yup.string().notRequired()
});

const passwordSchema = Yup.object().shape({
  email: Yup.string().email('E-mail inválido').required('Campo obrigatório'),
  password: Yup.string().required('Campo obrigatório'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Ops! As senhas devem ser iguais')
    .required(t('warning.requiredField'))
});

const schoolSchema = Yup.object().shape({
  school: Yup.string().required(t('warning.requiredField')),
  gradeAndShift: Yup.string().required(t('warning.requiredField')),
  class: Yup.string().required(t('warning.requiredField'))
});

const termsSchema = Yup.object().shape({
  terms: Yup.string().required('É necessário aceitar os termos.')
});

export default function StudentSignUp() {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({});
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [passwordIsStrong, setPasswordIsStrong] = useState(false);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [classOptions, setClassOptions] = useState([]);
  const [gradeAndShiftOptions, setGradeAndShiftOptions] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedGradeAndShift, setSelectedGradeAndShift] = useState(null);
  const [errors, setErrors] = useState({});

  const params = useParams();
  const link = params.link;

  useEffect(() => {
    const pathname = window.location.pathname;

    if (pathname.includes('/aluno/')) {
      const newPath = pathname.replace('/aluno', '');
      navigate(newPath);
    }
  }, []);

  useEffect(() => {
    async function fetchData() {
      async function fetchInstitutions() {
        const response = await api.get(`/institutions`);
        const institutions = response.data.map(institution => {
          return { value: institution.attributes.id, label: institution.attributes.name };
        });
        return institutions;
      }

      async function fetchCourses() {
        const response = await api.get(`/courses/simple?filter[is_public]=false`);
        const courses = response.data.map(course => {
          return { value: course.attributes.id, label: course.attributes.title };
        });
        return courses;
      }

      try {
        const [institutions, courses] = await Promise.all([fetchInstitutions(), fetchCourses()]);
        setSchoolOptions(institutions);
        setGradeAndShiftOptions(courses);
      } catch (error) {
        console.error('Erro ao carregar dados iniciais:', error);
        toast.error('Erro ao carregar opções de cadastro.');
      }
    }

    fetchData();
  }, []);

  useEffect(() => {
    if (link) {
      navigate(`/criar-uma-conta/${link}/nome`);
    }
  }, [link]);

  useEffect(() => {
    async function fetchClassesData() {
      async function fetchClasses() {
        const response = await api.get(`/classrooms?filter[institution_id]=${selectedSchool}&filter[course_id]=${selectedGradeAndShift}`);
        const classes = response.data.map(classe => {
          return { value: classe.attributes.id, label: classe.attributes.title };
        });
        return classes;
      }

      if (selectedSchool && selectedGradeAndShift) {
        try {
          const classes = await fetchClasses();
          setClassOptions(classes);
        } catch (error) {
          console.error('Erro ao carregar classes:', error);
          toast.error('Erro ao carregar classes.');
        }
      } else {
        setClassOptions([]);
      }
    }

    fetchClassesData();
  }, [selectedSchool, selectedGradeAndShift]);

  const { formStep } = useParams();
  const cfpRef = useMask({
    mask: '___.___.___-__',
    replacement: { _: /\d/ }
  });
  const cellPhoneRef = useMask({
    mask: '(__) _____-____',
    replacement: { _: /\d/ }
  });
  const passwordRef = React.createRef();
  const confirmPasswordRef = React.createRef();

  function handleFormValues(values) {
    setFormValues({ ...formValues, ...values });
  }

  const handleNext = async (values, actions) => {
    handleFormValues(values);

    if (formStep === 'nome') {
      const cpf = values.cpf.replace(/\D/g, '');
      const response = await checkUserCpf(cpf);
      if (response.id) {
        actions.setFieldError('cpf', 'Este CPF já está cadastrado.');
        return;
      }
      if (link) {
        navigate(`/criar-uma-conta/${link}/senha`);
      } else {
        navigate('/criar-uma-conta/senha');
      }
    }

    if (formStep === 'senha') {
      const email = values.email;
      const response = await checkEmail(email);

      const { found } = response;

      if (found) {
        actions.setFieldError('email', 'Este email já está cadastrado. Termine seu cadastro com um email diferente ou solicite nova senha para este email.');
        return;
      }
      if (link) {
        navigate(`/criar-uma-conta/${link}/termos`);
      } else {
        navigate('/criar-uma-conta/escola');
      }
    }

    if (formStep === 'escola') {
      navigate('/criar-uma-conta/termos');
    }

    if (formStep === 'termos') {
      toast.loading('Enviando dados...');
      const studentFormated = link ? formatStudentData({ ...formValues, link }) : formatStudentData({ ...formValues });
      try {
        let response;
        response = await createStudent(studentFormated);
        toast.dismiss();
        if (response.data) {
          toast.success('Cadastro realizado com sucesso');
          link ? navigate(`/criar-uma-conta/${link}/conta-criada`) : navigate('/criar-uma-conta/conta-criada');
        }
      } catch (error) {
        toast.dismiss();
        toast.error('Erro ao enviar dados. Tente novamente mais tarde.');
      } finally {
        actions.setSubmitting(false);
      }
    }
  };

  const handleBackButton = () => {
    if (formStep === 'nome') {
      return link ? `/login/${link}` : '/login';
    } else if (formStep === 'senha') {
      if (link) {
        return `/criar-uma-conta/${link}/nome`;
      } else {
        return '/criar-uma-conta/nome';
      }
    } else if (formStep === 'escola') {
      return '/criar-uma-conta/senha';
    } else if (formStep === 'termos') {
      if (link) {
        return `/criar-uma-conta/${link}/senha`;
      } else {
        return '/criar-uma-conta/escola';
      }
    }
  };

  return (
    <LoginLayout back={handleBackButton()}>
      <FormStep show={formStep === 'nome'}>
        <Formik
          initialValues={{ name: formValues.name, cellPhone: formValues.cellPhone ?? '', cpf: formValues.cpf ?? '' }}
          validationSchema={nameSchema}
          onSubmit={handleNext}
        >
          {props => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>
                    <b>Certo, vamos fazer o seu cadastro</b>
                    <p className="mb-0">Informe seu nome completo, telefone, estado e município</p>
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="name">*Nome completo</FormLabel>
                  <FormControl
                    id="name"
                    name="name"
                    type="text"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.name}
                    placeholder="Insira seu nome completo"
                  />
                </FormRow>
                <div className="flex gap-3">
                  <FormRow>
                    <FormLabel htmlFor="cellPhone">*Telefone com DDD</FormLabel>
                    <FormControl
                      id="cellPhone"
                      name="cellPhone"
                      type="tel"
                      value={props.values.cellPhone}
                      invalid={props.errors.cellPhone && props.touched.cellPhone}
                      placeholder="(00) 00000-0000"
                      ref={cellPhoneRef}
                    />
                    <ErrorMessage name="cellPhone">{msg => <FormFeedback invalid={true}>{msg}</FormFeedback>}</ErrorMessage>
                  </FormRow>
                  <FormRow>
                    <FormLabel htmlFor="cpf">{cpfRequired && '*'}CPF</FormLabel>
                    <FormControl
                      id="cpf"
                      name="cpf"
                      type="text"
                      onChange={props.handleChange}
                      value={props.values.cpf}
                      invalid={props.errors.cpf && props.touched.cpf}
                      placeholder="000.000.000-00"
                      ref={cfpRef}
                    />
                    <ErrorMessage name="cpf">{msg => <FormFeedback invalid={true}>{msg}</FormFeedback>}</ErrorMessage>
                  </FormRow>
                </div>
              </div>
              <ButtonWrapper>
                <Button
                  modifiers={['outline']}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.name || !props.values.cellPhone || (cpfRequired === 'true' && !props.values.cpf)}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>
      <FormStep show={formStep === 'senha'}>
        <Formik
          initialValues={{ email: formValues.email, password: formValues.password ?? '', confirmPassword: formValues.confirmPassword ?? '' }}
          validationSchema={passwordSchema}
          onSubmit={handleNext}
        >
          {props => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>
                    <b>Muito bom!</b> Agora, você precisa de email e senha para acessar a plataforma. Informe abaixo:
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="email">*Email</FormLabel>
                  <FormControl
                    id="email"
                    name="email"
                    type="email"
                    onChange={props.handleChange}
                    onBlur={props.handleBlur}
                    value={props.values.email}
                    invalid={props.errors.email && props.touched.email}
                    placeholder="Insira seu e-mail"
                  />
                  <ErrorMessage name="email">{msg => <FormFeedback invalid={true}>{msg}</FormFeedback>}</ErrorMessage>
                  {errors?.email && <ErrorMessage name="email">{<FormFeedback invalid={true}>{errors.email}</FormFeedback>}</ErrorMessage>}
                </FormRow>
                <FormRow>
                  <FormLabel htmlFor="password">*Senha</FormLabel>
                  <PasswordStrength
                    show={showPasswordStrength}
                    password={props.values.password}
                    validPassword={setPasswordIsStrong}
                  >
                    <FormControl
                      id="password"
                      name="password"
                      type={props.values.showPassword ? 'text' : 'password'}
                      onChange={props.handleChange}
                      onFocus={() => {
                        setShowPasswordStrength(true);
                        passwordRef.current.removeAttribute('readOnly');
                      }}
                      onBlur={() => {
                        setShowPasswordStrength(false);
                        passwordRef.current.setAttribute('readOnly', '');
                      }}
                      value={props.values.password}
                      invalid={props.errors.password && props.touched.password}
                      placeholder="Insira sua senha"
                      ref={passwordRef}
                      autoComplete="off"
                      readOnly
                    />
                  </PasswordStrength>
                  <FormField>
                    <Field
                      type="checkbox"
                      name="showPassword"
                    />
                    Mostrar senha
                  </FormField>
                  <ErrorMessage name="password">{msg => <FormFeedback invalid={true}>{msg}</FormFeedback>}</ErrorMessage>
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="confirmPassword">*Confirme sua senha</FormLabel>
                  <FormControl
                    id="confirmPassword"
                    name="confirmPassword"
                    type={props.values.showConfirmPassword ? 'text' : 'password'}
                    onChange={props.handleChange}
                    onFocus={() => confirmPasswordRef.current.removeAttribute('readOnly')}
                    onBlur={() => confirmPasswordRef.current.setAttribute('readOnly', '')}
                    value={props.values.confirmPassword}
                    invalid={props.errors.confirmPassword && props.touched.confirmPassword}
                    placeholder="Insira sua senha"
                    ref={confirmPasswordRef}
                    autoComplete="off"
                    readOnly
                  />
                  <FormField>
                    <Field
                      type="checkbox"
                      name="showConfirmPassword"
                    />
                    Mostrar senha
                  </FormField>
                  <ErrorMessage name="confirmPassword">{msg => <FormFeedback invalid={true}>{msg}</FormFeedback>}</ErrorMessage>
                </FormRow>
              </div>
              <div className={`flex justify-between gap-2 ${styles.box} ${styles.description}`}>
                <HiOutlineLightBulb className="h-6 w-10 rounded-[50%] bg-[var(--color-warning)] p-[2px] text-center text-white" />

                <p className="mb-0">Toda a comunicação da plataforma será feita pelo email que você cadastrar. Anote-o para não esquecer.</p>
              </div>
              <ButtonWrapper>
                <Button
                  modifiers={['outline']}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.email || !props.values.password || !props.values.confirmPassword || !passwordIsStrong}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === 'escola'}>
        <Formik
          initialValues={{ school: formValues.school ?? '', gradeAndShift: formValues.gradeAndShift ?? '', class: formValues.class ?? '' }}
          validationSchema={schoolSchema}
          onSubmit={handleNext}
        >
          {props => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>Insira seus dados escolares</h1>
                </div>

                <FormRow>
                  <FormLabel>Escola</FormLabel>
                  <FormCustomSelect
                    values={props.values}
                    name="school"
                    options={schoolOptions}
                    onChange={setSelectedSchool}
                    placeholder="Selecione a escola"
                  />
                </FormRow>

                <FormRow disabled={!props.values.school}>
                  <FormLabel>Curso</FormLabel>
                  <FormCustomSelect
                    values={props.values}
                    name="gradeAndShift"
                    options={gradeAndShiftOptions}
                    onChange={setSelectedGradeAndShift}
                    placeholder="Selecione o curso"
                  />
                </FormRow>

                <FormRow disabled={!props.values.gradeAndShift}>
                  <FormLabel>Turma</FormLabel>
                  <FormCustomSelect
                    values={props.values}
                    name="class"
                    options={classOptions}
                    placeholder="Selecione a turma"
                  />
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={['outline']}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.school || !props.values.gradeAndShift || !props.values.class}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === 'termos'}>
        <Formik
          initialValues={{ terms: formValues.terms ?? true }}
          validationSchema={termsSchema}
          onSubmit={handleNext}
        >
          {props => (
            <Form>
              <div className={styles.body}>
                <div className={styles.textGroup}>
                  <h1 className={styles.title}>Por fim, nosso termos de uso e política de privacidade</h1>
                </div>

                <FormRow>
                  <FormTerms />
                </FormRow>

                <FormRow>
                  <FormField round>
                    <Field
                      type="checkbox"
                      name="terms"
                    />
                    <span>
                      Ao clicar em “Criar conta”, você concorda com os{' '}
                      <a
                        href="/termos-de-uso"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        termos de uso
                      </a>{' '}
                      e{' '}
                      <a
                        href="/politica-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        política de privacidade
                      </a>
                    </span>
                  </FormField>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={['primary']}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={!props.values.terms}
                >
                  Criar minha conta
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === 'conta-criada'}>
        <Formik>
          {props => (
            <Form>
              <div className={styles.body}>
                <FormRow className="text-[16px]">
                  <b>Tudo certo. Sua conta foi criada!</b>
                  <p>Enviamos um e-mail para você. Acesse-o para ativar sua conta</p>
                  <p className="text-primary">
                    <b>É muito bom ter você com a gente!</b>
                  </p>
                </FormRow>
              </div>
            </Form>
          )}
        </Formik>
      </FormStep>
    </LoginLayout>
  );
}
